import { useState, useEffect, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
  SERVICE_OPTIONS,
  ENV_OPTIONS,
  EVENT_OPTIONS,
  REPO_OPTIONS,
} from "./constants";
import { LogFilters } from "./types";
import { fuzzySearch } from "./fuzzy";
import { tokens } from "./tokens";

dayjs.extend(utc);
dayjs.extend(timezone);

// Short, human-readable label for a IANA timezone (e.g. "IST", "GMT+5:30").
// Falls back to the IANA name if the runtime can't resolve an abbreviation.
function tzAbbreviation(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

interface FiltersPanelProps {
  onFetch: (filters: LogFilters) => void;
  loading: boolean;
  // Number of logs to request per batch (Log Monitor: 500, Error Monitor: 750).
  limit?: number;
  // When set, the Event filter is fixed to this value and its dropdown is hidden
  // (Error Monitor pins this to "error").
  lockedEvent?: string;
  // When set, the Service filter is fixed to this value and its dropdown is hidden
  // (Error Monitor pins this to "all").
  lockedService?: string;
  // Message suggestions offered as a dropdown in the "Exclude tags / messages" box —
  // typically the distinct messages from the current response, so the user can pick
  // several to exclude without retyping. Free text entry still works alongside these.
  excludeOptions?: string[];
  // Extra suggestions for the "Include" box (typically the distinct messages from the
  // current response). Merged with the static catalog and de-duplicated.
  includeOptions?: string[];
  // Multi-select dropdown suggestions for the "Tags" box — the distinct tags from
  // the current response (deduped). Free text entry still works alongside these.
  tagOptions?: string[];
}

export default function FiltersPanel({
  onFetch,
  loading,
  limit = 500,
  lockedEvent,
  lockedService,
  excludeOptions = [],
  includeOptions = [],
  tagOptions = [],
}: FiltersPanelProps) {
  // Include suggestions come straight from the response messages, de-duplicated
  // (case-insensitive) so the same message never appears twice.
  const includeAllOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of includeOptions) {
      const key = (m || "").trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
    return out;
  }, [includeOptions]);
  const now = dayjs();
  const [fromDate, setFromDate] = useState<Dayjs>(now.subtract(1, "day"));
  const [toDate, setToDate] = useState<Dayjs>(now);
  const [fromTime, setFromTime] = useState<Dayjs>(now.subtract(1, "hour"));
  const [toTime, setToTime] = useState<Dayjs>(now);
  const [service, setService] = useState(lockedService ?? "all");
  const [env, setEnv] = useState("production");
  const [event, setEvent] = useState(lockedEvent ?? "all");
  const [tags, setTags] = useState<string[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  // Pending (typed-but-not-yet-Entered) text in each multi-value box.
  const [searchInput, setSearchInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  // Tags/messages to EXCLUDE (sent as excludeTag in the payload).
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [excludeInput, setExcludeInput] = useState("");
  // Only open the search suggestions once the user has typed something.
  const [searchOpen, setSearchOpen] = useState(false);

  // The pickers show wall-clock time in the user's local timezone; we convert it
  // to UTC before sending. Default to the browser-guessed zone, then refine from
  // the user's IP (e.g. "Asia/Kolkata" -> IST) once it resolves.
  const [tz, setTz] = useState<string>(() => dayjs.tz.guess());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const ipData = await res.json();
        if (!cancelled && ipData?.timezone) setTz(ipData.timezone);
      } catch {
        // Keep the browser-guessed timezone if IP lookup fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tzLabel = tzAbbreviation(tz);

  // Repo <-> Tags stay in sync both ways:
  //  - Selecting a repo preset fills the Tags chips with its tag(s).
  //  - The Repo dropdown value is DERIVED from the current tags, so editing the
  //    tags (add/remove) updates the dropdown; tags matching no preset (empty or
  //    custom) fall back to "All Repos".
  const sameTags = (a: string[], b: string[]) =>
    a.length === b.length && a.every((t) => b.includes(t));
  const repo = REPO_OPTIONS.find((r) => sameTags(r.tags, tags))?.value ?? "";

  const handleRepoChange = (value: string) => {
    setTags(REPO_OPTIONS.find((r) => r.value === value)?.tags ?? []);
  };

  const handleFetch = () => {
    // The pickers hold wall-clock date/time in `tz`. Combine each date+time pair,
    // interpret it in `tz`, then convert to UTC — date AND time must be derived
    // from the same instant so a zone offset can't desync them (e.g. IST 02:00
    // -> previous-day 20:30 UTC).
    const toUtc = (date: Dayjs, time: Dayjs) =>
      dayjs.tz(
        `${date.format("YYYY-MM-DD")} ${time.format("HH:mm")}`,
        "YYYY-MM-DD HH:mm",
        tz
      ).utc();
    const fromUtc = toUtc(fromDate, fromTime);
    const toUtcVal = toUtc(toDate, toTime);

    const payload: LogFilters = {
      fromDate: fromUtc.format("YYYY-MM-DD"),
      toDate: toUtcVal.format("YYYY-MM-DD"),
      fromTime: fromUtc.format("HH:mm"),
      toTime: toUtcVal.format("HH:mm"),
      service: lockedService ?? service,
      env,
      event: lockedEvent ?? event,
      limit,
    };
    // Include any pending typed text that wasn't committed to a chip via Enter,
    // and commit it into the chips so the UI reflects what was queried.
    const mergedTags = Array.from(
      new Set([...tags, ...(tagInput.trim() ? [tagInput.trim()] : [])])
    );
    const mergedSearch = Array.from(
      new Set([...searchTerms, ...(searchInput.trim() ? [searchInput.trim()] : [])])
    );
    const mergedExclude = Array.from(
      new Set([...excludeTags, ...(excludeInput.trim() ? [excludeInput.trim()] : [])])
    );
    if (tagInput.trim()) {
      setTags(mergedTags);
      setTagInput("");
    }
    if (searchInput.trim()) {
      setSearchTerms(mergedSearch);
      setSearchInput("");
    }
    if (excludeInput.trim()) {
      setExcludeTags(mergedExclude);
      setExcludeInput("");
    }

    // tag / search / excludeTag accept a string or string[] (arrays are AND-matched
    // by the backend). Send a single string for one value, an array for several, omit if empty.
    if (mergedTags.length === 1) payload.tag = mergedTags[0];
    else if (mergedTags.length > 1) payload.tag = mergedTags;
    if (mergedSearch.length === 1) payload.search = mergedSearch[0];
    else if (mergedSearch.length > 1) payload.search = mergedSearch;
    if (mergedExclude.length === 1) payload.excludeTag = mergedExclude[0];
    else if (mergedExclude.length > 1) payload.excludeTag = mergedExclude;
    onFetch(payload);
  };

  // Disable every control while a fetch is in flight; re-enables when loading clears.
  const tp = { size: "small" as const, fullWidth: true, disabled: loading };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.divider}`,
        borderRadius: 1,
      }}
    >
      {/* Multi-value Search + Tags row */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          disabled={loading}
          options={includeAllOptions}
          // Empty input: show the response messages (capped) so the list opens on
          // click; once typing, fuzzy-rank them.
          filterOptions={(opts, state) =>
            state.inputValue.trim()
              ? fuzzySearch(opts as string[], state.inputValue)
              : (opts as string[]).slice(0, 50)
          }
          filterSelectedOptions
          openOnFocus={includeAllOptions.length > 0}
          forcePopupIcon={includeAllOptions.length > 0}
          open={searchOpen}
          onOpen={() => setSearchOpen(true)}
          onClose={() => setSearchOpen(false)}
          value={searchTerms}
          onChange={(_, v) => setSearchTerms(v as string[])}
          inputValue={searchInput}
          onInputChange={(_, v) => setSearchInput(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  size="small"
                  label={option}
                  onClick={() => {
                    // Click a chip to edit it in place: drop its text back into the
                    // input (and remove the chip), then press Enter to re-add.
                    setSearchTerms((prev) => prev.filter((_, i) => i !== index));
                    setSearchInput(String(option));
                    setSearchOpen(true);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              );
            })
          }
          sx={{ flex: 1, minWidth: 300 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Include messages / request id / user id"
              placeholder="start typing…"
            />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          size="small"
          disabled={loading}
          options={tagOptions}
          // Dropdown of the response tags (deduped); free text still works too.
          openOnFocus={tagOptions.length > 0}
          filterOptions={(opts, state) =>
            state.inputValue.trim()
              ? fuzzySearch(opts as string[], state.inputValue)
              : (opts as string[]).slice(0, 50)
          }
          filterSelectedOptions
          forcePopupIcon={tagOptions.length > 0}
          value={tags}
          onChange={(_, v) => setTags(v as string[])}
          inputValue={tagInput}
          onInputChange={(_, v) => setTagInput(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  size="small"
                  label={option}
                  onClick={() => {
                    setTags((prev) => prev.filter((_, i) => i !== index));
                    setTagInput(String(option));
                  }}
                  sx={{ cursor: "pointer" }}
                />
              );
            })
          }
          sx={{ flex: 1, minWidth: 300 }}
          renderInput={(params) => (
            <TextField {...params} label="Tags (Enter to add)" placeholder="add tag…" />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          size="small"
          disabled={loading}
          options={excludeOptions}
          // Only opt into the dropdown behaviour when suggestions were supplied
          // (Error Monitor). With no options (Log Monitor) this stays exactly as
          // before: a plain free-text box, no popup icon, no open-on-focus.
          openOnFocus={excludeOptions.length > 0}
          // Empty input: offer the response messages (capped) so the user can browse
          // and pick several. Once they type, fuzzy-rank the same list.
          filterOptions={(opts, state) =>
            state.inputValue.trim()
              ? fuzzySearch(opts as string[], state.inputValue)
              : (opts as string[]).slice(0, 50)
          }
          filterSelectedOptions
          forcePopupIcon={excludeOptions.length > 0}
          value={excludeTags}
          onChange={(_, v) => setExcludeTags(v as string[])}
          inputValue={excludeInput}
          onInputChange={(_, v) => setExcludeInput(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  size="small"
                  label={option}
                  onClick={() => {
                    setExcludeTags((prev) => prev.filter((_, i) => i !== index));
                    setExcludeInput(String(option));
                  }}
                  sx={{ cursor: "pointer" }}
                />
              );
            })
          }
          sx={{ flex: 1, minWidth: 280 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Exclude tags / messages (Enter to add)"
              placeholder="exclude…"
            />
          )}
        />
      </Box>

      {/* Date / time / dropdowns row — fields flex/shrink so Fetch stays on the same line */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 1,
            alignItems: "center",
          }}
        >
        <DatePicker
          label="From"
          value={fromDate}
          onChange={(v) => v && setFromDate(v)}
          disabled={loading}
          slotProps={{ textField: { ...tp } }}
        />
        <TimePicker
          label={`Time (${tzLabel})`}
          value={fromTime}
          onChange={(v) => v && setFromTime(v)}
          ampm={false}
          disabled={loading}
          slotProps={{ textField: { ...tp } }}
        />
        <DatePicker
          label="To"
          value={toDate}
          onChange={(v) => v && setToDate(v)}
          disabled={loading}
          slotProps={{ textField: { ...tp } }}
        />
        <TimePicker
          label={`Time (${tzLabel})`}
          value={toTime}
          onChange={(v) => v && setToTime(v)}
          ampm={false}
          disabled={loading}
          slotProps={{ textField: { ...tp } }}
        />
        {!lockedService && (
          <TextField
            select
            label="Service"
            {...tp}
            value={service}
            onChange={(e) => setService(e.target.value)}
          >
            {SERVICE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Repo"
          {...tp}
          value={repo}
          onChange={(e) => handleRepoChange(e.target.value)}
        >
          {REPO_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Environment"
          {...tp}
          value={env}
          onChange={(e) => setEnv(e.target.value)}
        >
          {ENV_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        {!lockedEvent && (
          <TextField
            select
            label="Event"
            {...tp}
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          >
            {EVENT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        </Box>
        <Button
          variant="contained"
          onClick={handleFetch}
          disabled={loading}
          disableElevation
          sx={{ height: 40, minWidth: 90, flexShrink: 0 }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Fetch"}
        </Button>
      </Box>
    </Box>
  );
}
